import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { QueryFilter } from '@mariana/labos-client';
import { VIEW_REGISTRY, COMBINED_ANALYTICAL_COLUMNS } from '../config/viewsConfig';
import type { ColumnDef } from '../config/viewsConfig';
import { useLocalViewState } from '../hooks/useLocalViewState';
import { useCombinedAnalytical } from '../hooks/useCombinedAnalytical';
import { usePersistedColumns } from '../hooks/usePersistedColumns';
import { EntityFilters } from './EntityFilters';
import { EntityTable } from './EntityTable';
import { CopyLinkButton } from './CopyLinkButton';
import { Breadcrumb } from './Breadcrumb';
import { ViewSearchBar } from './ViewSearchBar';
import { useLab, entityHasLabId } from '../LabContext';
import './AnalyticalViewer.css';
import './EntityView.css';

const SECTIONS = ['nmr', 'icp', 'shimadzu'] as const;

function ChevronIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg
      className={`analytical-section-chevron${collapsed ? ' collapsed' : ''}`}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="4 6 8 10 12 6" />
    </svg>
  );
}

interface AnalyticalTableSectionProps {
  viewId: string;
  extraFilters: QueryFilter[];
  search: string | undefined;
  onEntityClick: (entityId: number) => void;
}

function AnalyticalTableSection({ viewId, extraFilters, search, onEntityClick }: AnalyticalTableSectionProps) {
  const view = VIEW_REGISTRY[viewId];
  const [collapsed, setCollapsed] = useState(false);

  const {
    entities,
    resolvedEntities,
    pagination,
    sortBy,
    sortOrder,
    loading,
    handlePageChange,
    handleLimitChange,
    handleSortChange,
    visibleColumns,
    setVisibleColumns,
    allColumns,
    fetchAllEntities,
  } = useLocalViewState({
    entityType: view.entityType,
    allColumns: view.columns,
    initialFilters: view.filters,
    defaultSort: view.defaultSort,
    extraFilters,
    search,
    initialLimit: 15,
    storageKey: `sv-${viewId}`,
  });

  return (
    <div className="analytical-section">
      <div className="analytical-section-header" onClick={() => setCollapsed(c => !c)}>
        <ChevronIcon collapsed={collapsed} />
        <h3 className="analytical-section-title">
          {view.label} Results
          <span className="analytical-section-count"> ({pagination.count.toLocaleString()})</span>
        </h3>
      </div>
      {!collapsed && (
        <div className="analytical-section-body">
          <EntityTable
            entities={entities}
            entityType={view.entityType}
            columns={visibleColumns}
            defaultColumns={allColumns}
            onColumnsChange={setVisibleColumns}
            resolvedEntities={resolvedEntities}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onEntityClick={onEntityClick}
            onSortChange={handleSortChange}
            sortBy={sortBy}
            sortOrder={sortOrder}
            loading={loading}
            exportName={`analytical-${viewId}`}
            onExportAll={fetchAllEntities}
            pageSizeOptions={[15, 25, 50, 100]}
            extraFilters={extraFilters}
          />
        </div>
      )}
    </div>
  );
}

interface CombinedTableSectionProps {
  extraFilters: QueryFilter[];
  search: string | undefined;
  onEntityClick: (entityId: number) => void;
}

function CombinedTableSection({ extraFilters, search, onEntityClick }: CombinedTableSectionProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { columns: visibleColumns, setColumns: setVisibleColumns } = usePersistedColumns(
    'sv-combined', COMBINED_ANALYTICAL_COLUMNS,
  );

  const {
    rows,
    pagination,
    loading,
    resolvedEntities,
    handlePageChange,
    handleLimitChange,
    fetchAllRows,
  } = useCombinedAnalytical({ extraFilters, search, initialLimit: 15 });

  const handleExportAll = useCallback(async (
    onProgress: (fetched: number, total: number) => void,
  ) => {
    return fetchAllRows(onProgress);
  }, [fetchAllRows]);

  return (
    <div className="analytical-section">
      <div className="analytical-section-header" onClick={() => setCollapsed(c => !c)}>
        <ChevronIcon collapsed={collapsed} />
        <h3 className="analytical-section-title">
          Combined Results
          <span className="analytical-section-count"> ({pagination.count.toLocaleString()})</span>
        </h3>
      </div>
      {!collapsed && (
        <div className="analytical-section-body">
          <EntityTable
            entities={rows as unknown as Parameters<typeof EntityTable>[0]['entities']}
            entityType="test"
            columns={visibleColumns}
            defaultColumns={COMBINED_ANALYTICAL_COLUMNS}
            onColumnsChange={setVisibleColumns}
            resolvedEntities={resolvedEntities}
            pagination={pagination}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            onEntityClick={onEntityClick}
            loading={loading}
            exportName="analytical-combined"
            onExportAll={handleExportAll as Parameters<typeof EntityTable>[0]['onExportAll']}
            pageSizeOptions={[15, 25, 50, 100]}
            extraFilters={extraFilters}
          />
        </div>
      )}
    </div>
  );
}

export function AnalyticalViewer() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { labFilters } = useLab();
  const hasLabId = entityHasLabId('test');

  const [filters, setFilters] = useState<QueryFilter[]>(() => {
    const filtersParam = searchParams.get('filters');
    if (filtersParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(filtersParam));
        if (Array.isArray(parsed)) return parsed;
      } catch { /* ignore */ }
    }
    return [];
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleFiltersChange = useCallback((newFilters: QueryFilter[]) => {
    setFilters(newFilters);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (newFilters.length > 0) {
        next.set('filters', encodeURIComponent(JSON.stringify(newFilters)));
      } else {
        next.delete('filters');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const extraFilters = useMemo(() => {
    const combined = [...filters];
    if (hasLabId) combined.push(...labFilters);
    return combined;
  }, [filters, hasLabId, labFilters]);

  const handleEntityClick = useCallback((entityId: number) => {
    navigate(`/test/${entityId}`);
  }, [navigate]);

  const handleSampleClick = useCallback((entityId: number) => {
    navigate(`/sample/${entityId}`);
  }, [navigate]);

  return (
    <div className="entity-view analytical-viewer">
      <div className="entity-view-header">
        <Breadcrumb items={[
          { label: 'Mariana Labs', path: '/dashboard' },
          { label: 'Views' },
          { label: 'Sample Viewer' },
        ]} />
        <p className="fv-description">Consolidated analytical results across NMR, ICP, and Shimadzu instruments</p>
      </div>

      <div className="entity-filters-row">
        <EntityFilters
          entityType="test"
          filters={filters}
          onFiltersChange={handleFiltersChange}
          extraFilters={extraFilters}
        />
        <CopyLinkButton />
      </div>

      <ViewSearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        loading={false}
      />

      {SECTIONS.map(viewId => (
        <AnalyticalTableSection
          key={viewId}
          viewId={viewId}
          extraFilters={extraFilters}
          search={debouncedSearch || undefined}
          onEntityClick={handleEntityClick}
        />
      ))}

      <CombinedTableSection
        extraFilters={extraFilters}
        search={debouncedSearch || undefined}
        onEntityClick={handleSampleClick}
      />
    </div>
  );
}
